/**
 * Metric Card Component
 * Reusable metric card with skeleton loading and error states
 */

'use client';

import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/Skeleton';
import { LucideIcon, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MetricCardProps {
  icon: LucideIcon;
  value: number | string;
  label: string;
  subtitle?: string;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  className?: string;
}

export function MetricCard({
  icon: Icon,
  value,
  label,
  subtitle,
  isLoading = false,
  isError = false,
  onRetry,
  className,
}: MetricCardProps) {
  return (
    <Card
      className={cn(
        'p-4 sm:p-5 md:p-6 text-center hover:shadow-lg transition-all group border-l-4 border-l-primary/30 hover:border-l-primary/60 bg-gradient-to-br from-card to-card/95',
        className
      )}
      role="region"
      aria-label={`${label}: ${isLoading ? 'Loading' : isError ? 'Error' : value}`}
      aria-live="polite"
      aria-busy={isLoading}
    >
      <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2 sm:mb-3">
        <Icon
          className="h-4 w-4 sm:h-5 sm:w-5 text-primary/80 group-hover:text-primary transition-colors"
          aria-hidden="true"
        />
        {isLoading ? (
          <Skeleton variant="text" className="h-8 w-14 sm:h-9 sm:w-16" />
        ) : isError ? (
          <div
            className="text-3xl sm:text-4xl font-bold text-destructive"
            aria-label="Error loading data"
          >
            —
          </div>
        ) : (
          <div
            className="text-3xl sm:text-4xl font-bold text-primary tabular-nums"
            aria-label={`${value} ${label}`}
          >
            {value}
          </div>
        )}
      </div>
      <div className="text-xs sm:text-sm font-semibold text-foreground mb-1">{label}</div>
      {isLoading ? (
        <Skeleton variant="text" className="h-3 w-24 mx-auto mt-1" />
      ) : isError ? (
        <div className="text-xs text-destructive/80 mt-1 flex items-center justify-center gap-1">
          <AlertCircle className="h-3 w-3" aria-hidden="true" />
          <span>Failed to load</span>
          {onRetry && (
            <button
              onClick={onRetry}
              className="underline hover:no-underline ml-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              aria-label="Retry loading data"
            >
              Retry
            </button>
          )}
        </div>
      ) : (
        subtitle && (
          <div
            className="text-xs text-muted-foreground mt-1.5 leading-relaxed"
            aria-label={subtitle}
          >
            {subtitle}
          </div>
        )
      )}
    </Card>
  );
}
